import { useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '@liminal/web-ui/feedback/LoadingSpinner';
import { useDebounce } from '@/hooks/useDebounce';
import { adminClient } from '@/lib/adminClient';
import { userKeys } from '@/hooks/useUsers';
import type { EnrichedProfile } from '@/api/types';
import { AdminSortDirection, AdminUserSortField } from '@/api/generated';

/** One user the picker found, labelled the way the operator searched for them. */
export interface UserOption {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  displayTag: string;
  /** Email, name and @handle, whichever the user has, or the id. */
  label: string;
}

function profileToOption(ep: EnrichedProfile): UserOption | null {
  const p = ep.profile;
  if (!p?.userId) return null;
  const email = p.email ?? '';
  const firstName = p.firstName ?? '';
  const lastName = p.lastName ?? '';
  const displayTag = p.displayTag ?? '';
  const name = `${firstName} ${lastName}`.trim();
  const handle = displayTag ? `@${displayTag}` : '';
  const label = [email, name, handle].filter(Boolean).join(' — ') || p.userId;
  return {
    userId: p.userId,
    email,
    firstName,
    lastName,
    displayTag,
    label,
  };
}

interface UserPickerProps {
  value: UserOption | null;
  onChange: (user: UserOption | null) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  /** Shown under the field, which turns red. */
  error?: string;
}

/**
 * Picks one user by email, name or @handle, through the users search: ten
 * matches once two characters are typed, and no search for the pick itself.
 *
 * An MUI Autocomplete, whose `renderInput` must be a TextField, which is why
 * this file is exempt from the shared-form-controls rule
 * (application/web/admin/CLAUDE.md).
 */
export function UserPicker({ value, onChange, label = 'User', required, disabled, error }: UserPickerProps) {
  const [userQuery, setUserQuery] = useState('');
  const debouncedUserQuery = useDebounce(userQuery, 250);
  const searchQuery = debouncedUserQuery.trim().replace(/^@/, '');
  // Once a user is picked the field shows their label, which is not a search.
  const showingPick = value !== null && debouncedUserQuery === value.label;

  // No sort is sent, so the key names the server's default — the ordering the
  // response actually comes back in. A typeahead does not care which order ten
  // matches arrive in, but the cache key still has to describe the request.
  const usersQuery = useQuery({
    queryKey: userKeys.list(
      searchQuery,
      10,
      0,
      '',
      '',
      AdminUserSortField.ADMIN_USER_SORT_FIELD_CREATED_AT,
      AdminSortDirection.ADMIN_SORT_DIRECTION_DESC,
    ),
    queryFn: () => adminClient.searchUsers({ query: searchQuery, limit: 10, offset: 0 }),
    enabled: searchQuery.length >= 2 && !showingPick,
  });

  // The pick stays among the options while no search runs, so the
  // Autocomplete can still match its value.
  const userOptions = useMemo<UserOption[]>(() => {
    const profiles = usersQuery.data?.profiles ?? [];
    const found = profiles
      .map(profileToOption)
      .filter((opt): opt is UserOption => opt !== null);
    return value && !found.some((opt) => opt.userId === value.userId) ? [value, ...found] : found;
  }, [usersQuery.data, value]);

  return (
    <Autocomplete
      options={userOptions}
      getOptionLabel={(opt) => opt.label}
      isOptionEqualToValue={(a, b) => a.userId === b.userId}
      value={value}
      onChange={(_, next) => onChange(next)}
      inputValue={userQuery}
      onInputChange={(_, next) => setUserQuery(next)}
      loading={usersQuery.isFetching}
      disabled={disabled}
      noOptionsText={searchQuery.length < 2 ? 'Type at least 2 characters' : 'No users found'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search by email, name, or @handle"
          size="small"
          required={required}
          error={error !== undefined}
          helperText={error}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {usersQuery.isFetching ? <LoadingSpinner variant="inline" /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
